import { Task, CreateTaskData, UpdateTaskData, TaskSearchParams } from '../types/task';
import { fetchApi } from '../lib/apiClient'; // Import the new API client
import { getPlatformInfo } from '../utils/platformUtils';

export class TaskService {
  static async getTasks(): Promise<Task[]> {
    // 获取平台信息用于调试
    const platformInfo = getPlatformInfo();

    // Android 平台特殊日志
    if (platformInfo.isAndroid) {
      console.log('[Android TaskService] Starting getTasks request', {
        platform: platformInfo.os,
        isNative: platformInfo.isNative,
        userAgent: platformInfo.userAgent,
        timestamp: new Date().toISOString()
      });
    }

    try {
      // Use fetchApi with relative path and GET method
      const result = await fetchApi<Task[]>('/task/list', { method: 'GET' });

      // Android 平台成功日志
      if (platformInfo.isAndroid) {
        console.log('[Android TaskService] getTasks success', {
          taskCount: result?.length || 0,
          hasData: !!result,
          timestamp: new Date().toISOString()
        });
      }

      return result;
    } catch (error) {
      // Android 平台错误日志
      if (platformInfo.isAndroid) {
        console.error('[Android TaskService] getTasks failed', {
          error: error instanceof Error ? error.message : String(error),
          stack: error instanceof Error ? error.stack : undefined,
          userAgent: platformInfo.userAgent,
          timestamp: new Date().toISOString()
        });
      }
      throw error;
    }
  }

  static async getTask(id: string): Promise<Task> {
    // Use fetchApi with query parameter
    return await fetchApi<Task>(`/task/get?id=${id}`, { method: 'GET' });
  }

  static async createTask(taskData: CreateTaskData): Promise<Task> {
    // Use fetchApi with POST method and body
    return await fetchApi<Task>('/task/create', {
      method: 'POST',
      body: taskData, // fetchApi will stringify this
    });
  }

  static async updateTask(taskData: UpdateTaskData): Promise<Task> {
    // 添加错误处理和日志
    try {
      console.log('[TaskService] 更新任务:', taskData.id);

      // 使用 fetchApi 发送请求
      const updatedTask = await fetchApi<Task>('/task/update', {
        method: 'POST',
        body: taskData,
      });

      console.log('[TaskService] 任务更新成功:', updatedTask.id);
      return updatedTask;
    } catch (error) {
      console.error('[TaskService] 更新任务失败:', error);
      throw error;
    }
  }

  static async deleteTask(id: string): Promise<{ success: boolean; message: string; id: string }> {
    console.log('TaskService.deleteTask - 正在删除任务:', id);
    if (!id) {
      throw new Error('任务ID不能为空');
    }

    const trimmedId = id.trim();
    const uuidRegex = /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/;
    if (!uuidRegex.test(trimmedId)) {
      console.error('TaskService.deleteTask - 无效的任务ID格式:', trimmedId);
      throw new Error(`无效的任务ID格式: ${trimmedId}`);
    }

    // Use fetchApi with POST method and body containing the id
    const result = await fetchApi<{ success: boolean; message: string; id: string }>(
      '/task/delete',
      {
        method: 'POST',
        body: { id: trimmedId }, // Send ID in the body
      },
    );

    console.log('TaskService.deleteTask - 删除成功');
    return result;
  }

  static async searchTasks(params: TaskSearchParams): Promise<Task[]> {
    // Use fetchApi with POST method and body
    return await fetchApi<Task[]>('/task/search', {
      method: 'POST',
      body: params,
    });
  }
}